// apps/web/src/lib/estoque/inventario.ts — Fase 13, Plano 27 (EST-13, D-24, D-26).
// Lógica pura da contagem de inventário: apresentação da situação, progresso, "Não contado" x 0, leitura estrita dos
// números digitados, validação do envio e texto da confirmação. Nada aqui busca ou deduz saldo: a contagem é CEGA e o
// front só conhece `ItemContagemCega` (sem saldo, esperado ou corte). Números pt-BR via `interpretarNumeroPtBr`
// (sem adivinhar milhar); quantidade até 4 casas e custo até 6, como a API.
import { SITUACOES_INVENTARIO, interpretarNumeroPtBr, mensagemNumeroAmbiguo } from '@pieltec/contracts';
import type { CriarInventarioInput, DonoTipo, InventarioContagemView, ItemContagemCega, SituacaoInventario } from '@pieltec/contracts';
import { paraTexto } from '../vendas/numero';

export const PERMISSAO_INVENTARIAR = 'estoque:inventariar';
export const PERMISSAO_APROVAR_AJUSTE = 'estoque:aprovar-ajuste';

const CASAS_QUANTIDADE = 4;
const CASAS_CUSTO = 6;
/** Mesmo teto numérico do DTO da API (NUMERIC(15,x)). */
const LIMITE_NUMERICO = 99_999_999_999;

export const MENSAGEM_NAO_CONTADO = 'Não contado';
export const MENSAGEM_CUSTO_OBRIGATORIO = 'Informe o custo.';
export const MENSAGEM_SEM_CONTAGEM = 'Conte pelo menos um item antes de enviar.';
export const MENSAGEM_CORRIGIR_CAMPOS = 'Corrija os campos destacados antes de enviar.';

// ---------------------------------------------------------------------------------------------------------------------
// Situação (badge com ícone e texto, UI-SPEC "Inventário — situação")
// ---------------------------------------------------------------------------------------------------------------------

export type IconeSituacaoInventario = 'ListChecks' | 'Hourglass' | 'CheckCircle2' | 'XCircle';

export interface ApresentacaoSituacaoInventario {
  readonly rotulo: string;
  readonly icone: IconeSituacaoInventario;
  readonly classes: string;
}

const APRESENTACAO: Readonly<Record<SituacaoInventario, ApresentacaoSituacaoInventario>> = {
  EM_CONTAGEM: { rotulo: 'Em contagem', icone: 'ListChecks', classes: 'bg-blue-50 text-blue-700 border-blue-200' },
  AGUARDANDO_APROVACAO: { rotulo: 'Aguardando aprovação', icone: 'Hourglass', classes: 'bg-amber-50 text-amber-700 border-amber-200' },
  AJUSTADO: { rotulo: 'Ajustado', icone: 'CheckCircle2', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  DESCARTADO: { rotulo: 'Descartado', icone: 'XCircle', classes: 'bg-slate-100 text-slate-500 border-slate-200' },
};

/** Situação desconhecida (versão nova do servidor) cai num badge neutro com o texto cru: nunca quebra a tela. */
export function apresentacaoSituacaoInventario(situacao: SituacaoInventario): ApresentacaoSituacaoInventario {
  return APRESENTACAO[situacao] ?? { rotulo: String(situacao), icone: 'ListChecks', classes: 'bg-slate-100 text-slate-500 border-slate-200' };
}

// ---------------------------------------------------------------------------------------------------------------------
// Números digitados
// ---------------------------------------------------------------------------------------------------------------------

export type LeituraNumero =
  | { readonly tipo: 'vazio' }
  | { readonly tipo: 'ok'; readonly valor: number }
  | { readonly tipo: 'erro'; readonly mensagem: string };

function casasDecimais(valor: number, casas: number): boolean {
  const escalado = valor * 10 ** casas;
  return Math.abs(escalado - Math.round(escalado)) <= 1e-6;
}

function lerNumero(texto: string, casas: number, rotulo: string): LeituraNumero {
  const lido = interpretarNumeroPtBr(texto);
  if (lido.tipo === 'vazio') return { tipo: 'vazio' };
  if (lido.tipo === 'invalido') return { tipo: 'erro', mensagem: `Informe ${rotulo} válido.` };
  if (lido.tipo === 'ambiguo') return { tipo: 'erro', mensagem: mensagemNumeroAmbiguo(texto, lido) };
  if (lido.valor < 0) return { tipo: 'erro', mensagem: `${rotulo[0]?.toUpperCase() ?? ''}${rotulo.slice(1)} não pode ser negativo.` };
  if (lido.valor > LIMITE_NUMERICO) return { tipo: 'erro', mensagem: 'Valor grande demais.' };
  if (!casasDecimais(lido.valor, casas)) return { tipo: 'erro', mensagem: `Use no máximo ${casas} casas decimais.` };
  return { tipo: 'ok', valor: lido.valor };
}

/** Quantidade contada digitada: vazio = "Não contado"; zero precisa ser digitado; até 4 casas; sem milhar adivinhado. */
export function parseQuantidade(texto: string): LeituraNumero {
  return lerNumero(texto, CASAS_QUANTIDADE, 'um número');
}

/** Custo inicial digitado (R$ por unidade): até 6 casas. */
export function parseCusto(texto: string): LeituraNumero {
  return lerNumero(texto, CASAS_CUSTO, 'um custo');
}

/** Valor do campo de contagem: '' (ou texto recusado) = null ("Não contado"), '0' = 0. */
export function valorCampoContagem(texto: string): number | null {
  const lido = parseQuantidade(texto);
  return lido.tipo === 'ok' ? lido.valor : null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Edição local da contagem (texto dos campos por linha) e progresso
// ---------------------------------------------------------------------------------------------------------------------

export interface EdicaoLinha {
  readonly quantidade: string;
  readonly custo: string;
}

/** Texto dos campos por `itemId`. Só existe no navegador até "Salvar rascunho". */
export type EdicaoContagem = Readonly<Record<string, EdicaoLinha>>;

export function edicaoDaLinha(item: ItemContagemCega): EdicaoLinha {
  return {
    quantidade: item.quantidadeContada === null ? '' : paraTexto(item.quantidadeContada),
    custo: item.custoInicial === null ? '' : paraTexto(item.custoInicial),
  };
}

/** Edição inicial: o que o servidor já guardou (o texto dos campos reflete a view). */
export function edicaoInicial(itens: readonly ItemContagemCega[]): EdicaoContagem {
  return Object.fromEntries(itens.map((item) => [item.itemId, edicaoDaLinha(item)]));
}

export interface Progresso {
  readonly contados: number;
  readonly total: number;
}

/** Contado = quantidade presente (zero conta; vazio não). */
export function progresso(itens: readonly { readonly quantidadeContada: number | null }[]): Progresso {
  return { contados: itens.filter((item) => item.quantidadeContada !== null).length, total: itens.length };
}

/** Progresso ao vivo: usa o que está digitado (campo vazio ou recusado não conta). */
export function progressoDaEdicao(itens: readonly ItemContagemCega[], edicao: EdicaoContagem): Progresso {
  return progresso(itens.map((item) => ({ quantidadeContada: valorCampoContagem((edicao[item.itemId] ?? edicaoDaLinha(item)).quantidade) })));
}

/** Largura (0..100) da faixa `role="progressbar"`. */
export function percentualProgresso(p: Progresso): number {
  return p.total === 0 ? 0 : Math.round((p.contados / p.total) * 100);
}

export function textoProgresso(p: Progresso): string {
  return `${p.contados} de ${p.total} itens contados`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Abertura e envio
// ---------------------------------------------------------------------------------------------------------------------

/** Abertura de matéria-prima própria: é onde o custo inicial é digitado (D-26). */
export function pedeCustoInicial(view: Pick<InventarioContagemView, 'eAbertura' | 'donoTipo'>): boolean {
  return view.eAbertura && view.donoTipo === 'PIELTEC';
}

export interface ErroLinha {
  readonly quantidade?: string;
  readonly custo?: string;
}

export interface ResultadoValidacaoEnvio {
  readonly ok: boolean;
  /** Mensagem geral (nenhum item contado / campos a corrigir); nula quando ok. */
  readonly mensagem: string | null;
  readonly erros: Readonly<Record<string, ErroLinha>>;
  readonly contados: number;
}

/**
 * Pode enviar? Exige >= 1 item contado e campos legíveis; na abertura própria, cada item contado com quantidade > 0
 * precisa de custo (digitado, ou a sugestão da última NF que o servidor congela no envio). O servidor revalida tudo.
 */
export function validarEnvio(
  view: Pick<InventarioContagemView, 'eAbertura' | 'donoTipo' | 'itens'>,
  edicao: EdicaoContagem,
): ResultadoValidacaoEnvio {
  const exigeCusto = pedeCustoInicial(view);
  const erros: Record<string, ErroLinha> = {};
  let contados = 0;
  for (const item of view.itens) {
    const linha = edicao[item.itemId] ?? edicaoDaLinha(item);
    const quantidade = parseQuantidade(linha.quantidade);
    const erro: { quantidade?: string; custo?: string } = {};
    if (quantidade.tipo === 'erro') erro.quantidade = quantidade.mensagem;
    if (quantidade.tipo === 'ok') contados += 1;
    if (exigeCusto) {
      const custo = parseCusto(linha.custo);
      if (custo.tipo === 'erro') erro.custo = custo.mensagem;
      else if (custo.tipo === 'vazio' && quantidade.tipo === 'ok' && quantidade.valor > 0 && item.custoSugeridoUltimaNf === null) {
        erro.custo = MENSAGEM_CUSTO_OBRIGATORIO;
      }
    }
    if (erro.quantidade !== undefined || erro.custo !== undefined) erros[item.itemId] = erro;
  }
  if (Object.keys(erros).length > 0) return { ok: false, mensagem: MENSAGEM_CORRIGIR_CAMPOS, erros, contados };
  if (contados === 0) return { ok: false, mensagem: MENSAGEM_SEM_CONTAGEM, erros, contados };
  return { ok: true, mensagem: null, erros, contados };
}

/** Erros de leitura dos campos digitados (sem as regras de envio): bloqueiam "Salvar rascunho" e "Enviar contagem". */
export function errosDeLeitura(itens: readonly ItemContagemCega[], edicao: EdicaoContagem, comCusto: boolean): Readonly<Record<string, ErroLinha>> {
  const erros: Record<string, ErroLinha> = {};
  for (const item of itens) {
    const linha = edicao[item.itemId] ?? edicaoDaLinha(item);
    const quantidade = parseQuantidade(linha.quantidade);
    const custo = comCusto ? parseCusto(linha.custo) : ({ tipo: 'vazio' } as const);
    const erro: { quantidade?: string; custo?: string } = {};
    if (quantidade.tipo === 'erro') erro.quantidade = quantidade.mensagem;
    if (custo.tipo === 'erro') erro.custo = custo.mensagem;
    if (erro.quantidade !== undefined || erro.custo !== undefined) erros[item.itemId] = erro;
  }
  return erros;
}

/** Máximo de linhas por PUT /contagem (o DTO da API recusa mais que isso). */
export const MAXIMO_ITENS_POR_SALVAMENTO = 500;

export interface LinhaSalvamento {
  readonly itemId: string;
  readonly quantidadeContada: number | null;
  readonly custoInicial?: number | null;
}

function valorOuNulo(leitura: LeituraNumero): number | null | undefined {
  if (leitura.tipo === 'ok') return leitura.valor;
  if (leitura.tipo === 'vazio') return null;
  return undefined;
}

/**
 * Linhas que mudaram em relação ao que o servidor já tem (corpo do PUT /contagem). Campo recusado (ambíguo, negativo...)
 * nunca é enviado: a tela mostra o erro e a linha fica de fora. `comCusto` só na abertura de matéria-prima própria
 * (fora dela o servidor recusa `custoInicial`).
 */
export function itensAlterados(original: readonly ItemContagemCega[], edicao: EdicaoContagem, opcoes: { readonly comCusto?: boolean } = {}): readonly LinhaSalvamento[] {
  const alteradas: LinhaSalvamento[] = [];
  for (const item of original) {
    const linha = edicao[item.itemId];
    if (!linha) continue;
    const quantidade = valorOuNulo(parseQuantidade(linha.quantidade));
    if (quantidade === undefined) continue;
    const custo = opcoes.comCusto ? valorOuNulo(parseCusto(linha.custo)) : undefined;
    const mudouQuantidade = quantidade !== item.quantidadeContada;
    const mudouCusto = custo !== undefined && custo !== item.custoInicial;
    if (!mudouQuantidade && !mudouCusto) continue;
    alteradas.push(custo === undefined ? { itemId: item.itemId, quantidadeContada: quantidade } : { itemId: item.itemId, quantidadeContada: quantidade, custoInicial: custo });
  }
  return alteradas;
}

/** Divide o salvamento em PUTs de no máximo 500 linhas (cada PUT sobe a `versao`). */
export function lotesDeSalvamento<T>(linhas: readonly T[], tamanho: number = MAXIMO_ITENS_POR_SALVAMENTO): readonly (readonly T[])[] {
  const lotes: T[][] = [];
  for (let i = 0; i < linhas.length; i += tamanho) lotes.push(linhas.slice(i, i + tamanho));
  return lotes;
}

/** Acima deste total de itens a grade renderiza só as linhas visíveis (centenas de itens no tablet). */
export const LIMITE_VIRTUALIZACAO_CONTAGEM = 200;

/** Linha seguinte ao pressionar Enter; nulo na última (o foco fica onde está). */
export function proximaLinha(indice: number, total: number): number | null {
  if (!Number.isInteger(indice) || indice < 0 || indice + 1 >= total) return null;
  return indice + 1;
}

/** Há algo digitado que ainda não foi salvo (inclusive texto recusado)? Decide o aviso ao sair da tela. */
export function temAlteracoesNaoSalvas(itens: readonly ItemContagemCega[], edicao: EdicaoContagem, comCusto: boolean): boolean {
  return itensAlterados(itens, edicao, { comCusto }).length > 0 || Object.keys(errosDeLeitura(itens, edicao, comCusto)).length > 0;
}

/** Mantém o que já foi digitado e acrescenta a edição inicial dos itens novos (item não listado adicionado). */
export function mesclarEdicao(atual: EdicaoContagem, itens: readonly ItemContagemCega[]): EdicaoContagem {
  return Object.fromEntries(itens.map((item) => [item.itemId, atual[item.itemId] ?? edicaoDaLinha(item)]));
}

/** Corpo da confirmação do envio (UI-SPEC): informa quantos itens sem contagem serão ignorados. */
export function textoConfirmacaoEnvio(contados: number, total: number): string {
  const ignorados = Math.max(0, total - contados);
  const base = `${contados} de ${total} itens contados.`;
  const ignorar =
    ignorados === 0
      ? ''
      : ignorados === 1
        ? ' 1 item sem contagem será ignorado (o saldo dele não muda).'
        : ` ${ignorados} itens sem contagem serão ignorados (o saldo deles não muda).`;
  return `${base}${ignorar} Depois de enviar, você não altera a contagem.`;
}

/** Itens sem contagem na edição atual (para o filtro "Só não contados"). */
export function naoContados(itens: readonly ItemContagemCega[], edicao: EdicaoContagem): readonly ItemContagemCega[] {
  return itens.filter((item) => valorCampoContagem((edicao[item.itemId] ?? edicaoDaLinha(item)).quantidade) === null);
}

// ---------------------------------------------------------------------------------------------------------------------
// Lista de inventários: filtros resolvidos no servidor e espelhados na URL
// ---------------------------------------------------------------------------------------------------------------------

export interface FiltrosInventarios {
  /** Vazio = todas as situações em andamento (ou todas, com `incluirEncerrados`). */
  readonly situacao: SituacaoInventario | '';
  /** UUID da localização; vazio = todas. */
  readonly localizacaoId: string;
  readonly donoTipo: DonoTipo | '';
  /** "Incluir ajustados e descartados" (desmarcada por padrão). */
  readonly incluirEncerrados: boolean;
}

export const TAMANHO_PAGINA_INVENTARIOS = 50;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function filtrosInventariosVazios(): FiltrosInventarios {
  return { situacao: '', localizacaoId: '', donoTipo: '', incluirEncerrados: false };
}

export function filtrosInventariosAtivos(filtros: FiltrosInventarios): boolean {
  return filtros.situacao !== '' || filtros.localizacaoId !== '' || filtros.donoTipo !== '' || filtros.incluirEncerrados;
}

/**
 * Query string (sem `?`) com só os filtros ativos. Usa `URLSearchParams` (nenhum valor é concatenado na mão). O servidor
 * esconde ajustados e descartados por padrão; `incluirEncerrados=true` só vai quando a caixa está marcada.
 */
export function montarQueryInventarios(filtros: FiltrosInventarios, opcoes: { readonly pagina?: number; readonly tamanhoPagina?: number } = {}): string {
  const params = new URLSearchParams();
  params.set('pagina', String(opcoes.pagina ?? 1));
  params.set('tamanhoPagina', String(opcoes.tamanhoPagina ?? TAMANHO_PAGINA_INVENTARIOS));
  if (filtros.situacao !== '') params.set('situacao', filtros.situacao);
  if (filtros.localizacaoId !== '') params.set('localizacaoId', filtros.localizacaoId);
  if (filtros.donoTipo !== '') params.set('donoTipo', filtros.donoTipo);
  if (filtros.incluirEncerrados) params.set('incluirEncerrados', 'true');
  return params.toString();
}

/** Filtros a partir de `location.search`; valor inválido volta ao padrão em vez de gerar 400 na API. */
export function lerFiltrosInventariosDaUrl(search: string): FiltrosInventarios {
  const params = new URLSearchParams(search);
  const situacao = params.get('situacao') ?? '';
  const localizacao = params.get('localizacao') ?? '';
  const dono = params.get('dono') ?? '';
  return {
    situacao: SITUACOES_INVENTARIO.find((s) => s === situacao) ?? '',
    localizacaoId: UUID.test(localizacao) ? localizacao : '',
    donoTipo: dono === 'PIELTEC' || dono === 'CLIENTE' ? dono : '',
    incluirEncerrados: params.get('encerrados') === '1',
  };
}

/** `?situacao=...` para `history.replaceState`; string vazia quando não há filtro (URL limpa). */
export function escreverFiltrosInventariosNaUrl(filtros: FiltrosInventarios): string {
  const params = new URLSearchParams();
  if (filtros.situacao !== '') params.set('situacao', filtros.situacao);
  if (filtros.localizacaoId !== '') params.set('localizacao', filtros.localizacaoId);
  if (filtros.donoTipo !== '') params.set('dono', filtros.donoTipo);
  if (filtros.incluirEncerrados) params.set('encerrados', '1');
  const texto = params.toString();
  return texto === '' ? '' : `?${texto}`;
}

/** Subtítulo da lista: "{N} inventários" (singular para 1). */
export function contagemInventarios(total: number): string {
  return total === 1 ? '1 inventário' : `${total} inventários`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Novo inventário (Sheet)
// ---------------------------------------------------------------------------------------------------------------------

export const APOIO_NAO_TRAVA = 'O inventário não trava o estoque. A diferença considera o que entrar e sair durante a contagem.';

export interface CamposNovoInventario {
  readonly localizacaoId: string;
  readonly donoTipo: DonoTipo;
  readonly clienteId: string;
  readonly escolherItens: boolean;
  readonly materialIds: readonly string[];
  readonly loteIds: readonly string[];
}

export function camposNovoInventarioVazios(): CamposNovoInventario {
  return { localizacaoId: '', donoTipo: 'PIELTEC', clienteId: '', escolherItens: false, materialIds: [], loteIds: [] };
}

export interface ErrosNovoInventario {
  readonly localizacaoId?: string;
  readonly clienteId?: string;
  readonly itens?: string;
}

export function validarNovoInventario(campos: CamposNovoInventario): { readonly ok: boolean; readonly erros: ErrosNovoInventario } {
  const erros: { localizacaoId?: string; clienteId?: string; itens?: string } = {};
  if (campos.localizacaoId === '') erros.localizacaoId = 'Escolha a localização.';
  if (campos.donoTipo === 'CLIENTE' && campos.clienteId === '') erros.clienteId = 'Escolha o cliente.';
  if (campos.escolherItens) {
    const escolhidos = campos.donoTipo === 'CLIENTE' ? campos.loteIds.length : campos.materialIds.length;
    if (escolhidos === 0) erros.itens = campos.donoTipo === 'CLIENTE' ? 'Escolha pelo menos um lote.' : 'Escolha pelo menos um item.';
  }
  return { ok: Object.keys(erros).length === 0, erros };
}

/** Corpo do POST: sem `itens` = todos os itens da localização (inventário sem filtro). */
export function montarCriarInventario(campos: CamposNovoInventario): CriarInventarioInput {
  const base = {
    localizacaoId: campos.localizacaoId,
    donoTipo: campos.donoTipo,
    donoClienteId: campos.donoTipo === 'CLIENTE' ? campos.clienteId : null,
  };
  if (!campos.escolherItens) return base;
  return { ...base, itens: campos.donoTipo === 'CLIENTE' ? { loteIds: campos.loteIds } : { materialIds: campos.materialIds } };
}

/**
 * Query do teste de "localização sem movimentos" (banner de abertura): linhas material x local (ou lotes) já movimentadas
 * naquele local. `total = 0` indica local sem movimentos do dono. O servidor decide de verdade (`eAbertura` na criação).
 */
export function queryLocalSemMovimentos(campos: Pick<CamposNovoInventario, 'localizacaoId' | 'donoTipo' | 'clienteId'>): { readonly rota: string; readonly query: string } | null {
  if (campos.localizacaoId === '') return null;
  const params = new URLSearchParams({ pagina: '1', tamanhoPagina: '1', localizacaoId: campos.localizacaoId, mostrarSemSaldo: 'true' });
  if (campos.donoTipo === 'CLIENTE') {
    if (campos.clienteId === '') return null;
    params.set('clienteId', campos.clienteId);
    return { rota: '/api/v1/estoque/saldos/clientes', query: params.toString() };
  }
  return { rota: '/api/v1/estoque/saldos/proprio', query: params.toString() };
}

export function urlDetalheInventario(id: string): string {
  return `/estoque/inventarios/detalhe?id=${encodeURIComponent(id)}`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Erros da API
// ---------------------------------------------------------------------------------------------------------------------

export const MENSAGEM_VERSAO_DESATUALIZADA = 'Este inventário foi alterado por outro usuário. Recarregue para ver as mudanças antes de salvar.';
export const MENSAGEM_FALHA_INVENTARIO = 'Não foi possível concluir a operação. Tente novamente.';

export interface ErroInventario {
  readonly status: number;
  readonly codigo: string | null;
  readonly mensagem: string;
  /** Conflito de versão ou de situação: a tela oferece "Recarregar inventário" (nada local é descartado antes do clique). */
  readonly conflitoDeVersao: boolean;
  /** INVENTARIO_ESCOPO_EM_USO: o inventário ativo que já cobre o local/item. */
  readonly inventarioConflitante: { readonly id: string; readonly numero: string } | null;
  readonly itemCodigo: string | null;
  /** Linha apontada pela API (justificativa obrigatória, custo de abertura, saldo insuficiente). */
  readonly itemId: string | null;
}

const MENSAGENS_POR_CODIGO: Readonly<Record<string, string>> = {
  INVENTARIO_VERSAO_DESATUALIZADA: MENSAGEM_VERSAO_DESATUALIZADA,
  INVENTARIO_NAO_EDITAVEL: 'A situação deste inventário mudou. Recarregue o inventário para ver a situação atual.',
  INVENTARIO_AUTOAPROVACAO: 'Quem fez a contagem não pode aprovar este ajuste.',
  INVENTARIO_NAO_ENVIADO: 'A revisão só existe depois que a contagem é enviada.',
  INVENTARIO_JUSTIFICATIVA_OBRIGATORIA: 'Informe a justificativa de cada linha com diferença.',
  INVENTARIO_JUSTIFICATIVA_INVALIDA: 'Uma justificativa se refere a uma linha que não foi contada. Recarregue o inventário.',
  INVENTARIO_MOTIVO_OBRIGATORIO: 'Informe o motivo do descarte.',
  INVENTARIO_SEM_CONTAGEM: MENSAGEM_SEM_CONTAGEM,
  CUSTO_ABERTURA_OBRIGATORIO: 'Informe o custo dos itens contados antes de enviar.',
  INVENTARIO_COM_FILTRO: 'Este inventário tem itens escolhidos e não aceita item não listado.',
  INVENTARIO_ITEM_JA_LISTADO: 'Este item já está na lista de contagem.',
  INVENTARIO_SEM_ITENS: 'Não há itens com saldo nesta localização. Escolha os itens que serão contados.',
};

function textoOuNulo(valor: unknown): string | null {
  return typeof valor === 'string' && valor.trim() !== '' ? valor : null;
}

/** Interpreta status + corpo (já lido) de um erro do inventário em mensagem pt-BR e pistas para a tela. */
export function interpretarErroInventario(status: number, corpo: unknown): ErroInventario {
  const dados = (corpo && typeof corpo === 'object' ? corpo : {}) as Record<string, unknown>;
  const codigo = textoOuNulo(dados.codigo);
  const mensagemApi = Array.isArray(dados.message) ? textoOuNulo(dados.message[0]) : textoOuNulo(dados.message);
  const conflitoDeVersao = codigo === 'INVENTARIO_VERSAO_DESATUALIZADA' || codigo === 'INVENTARIO_NAO_EDITAVEL';
  const inventarioId = textoOuNulo(dados.inventarioId);
  const inventarioNumero = textoOuNulo(dados.inventarioNumero);
  const inventarioConflitante = codigo === 'INVENTARIO_ESCOPO_EM_USO' && inventarioId ? { id: inventarioId, numero: inventarioNumero ?? inventarioId } : null;
  const base = { status, codigo, conflitoDeVersao, inventarioConflitante, itemCodigo: textoOuNulo(dados.itemCodigo), itemId: textoOuNulo(dados.itemId) };
  if (status === 403) return { ...base, mensagem: 'Você não tem permissão para esta ação.' };
  const porCodigo = codigo ? MENSAGENS_POR_CODIGO[codigo] : undefined;
  if (porCodigo) return { ...base, mensagem: porCodigo };
  return { ...base, mensagem: mensagemApi ?? MENSAGEM_FALHA_INVENTARIO };
}

export async function lerErroInventario(res: Response): Promise<ErroInventario> {
  let corpo: unknown = null;
  try {
    corpo = await res.json();
  } catch {
    // Corpo vazio ou não-JSON: vale a mensagem padrão.
  }
  return interpretarErroInventario(res.status, corpo);
}
